/**
 * Classification of a failed log-upload batch: bin it, or keep it for the next flush.
 *
 * Its own module so tests can reach it without pulling in the Supabase client (and the
 * env it needs) through sink.js.
 */

/** 4xx (except 408/429) will fail identically on every retry — never re-queue those rows. */
export function isPermanentUploadFailure(status) {
  return status >= 400 && status < 500 && status !== 408 && status !== 429;
}

/** RLS rejects an expired JWT as 403/42501, indistinguishable from a real permission loss. */
export function isAuthRejection(status) {
  return status === 401 || status === 403;
}

/**
 * Whether a failed batch is unrecoverable and should be dropped rather than re-queued.
 *
 * The distinction that matters is "not allowed" vs "could not ask".
 *
 * This is a foreground-resume race, not a festival problem. The JWT expires while iOS has
 * the app suspended; on wake the app fires requests before the session refresh completes,
 * with the network stack still coming up, so refreshSession() fails on the transient.
 * PostgREST rejects the stale token as 403/42501 and a bare isPermanentUploadFailure(403)
 * bins the batch. Seen twice in 24 hours: 21 rows at the Rose Bowl on 2026-09-05 with weak
 * service, and 5 rows on a 2026-09-06 walk with full service and a concurrent
 * "TypeError: Load failed" on the position publish. So an auth rejection we could not
 * re-authenticate through is treated as transient and the rows are kept.
 *
 * @param {number} status HTTP status from the upload attempt.
 * @param {boolean} reauthFailed True when a refresh was attempted and produced no fresh
 *   token — offline, or a genuinely dead session, which are indistinguishable from here.
 */
export function shouldDropBatch(status, reauthFailed) {
  if (isAuthRejection(status) && reauthFailed) {
    return false;
  }
  return isPermanentUploadFailure(status);
}
